import type { IDataRoomResumeRecord } from './types';

export class DataRoomResumeRecordHelper {
  private static readonly RESUME_KEY_PREFIX = 'commenda-ai:data-room-upload:';

  static resumeStorageKey(conversationId: string): string {
    return `${DataRoomResumeRecordHelper.RESUME_KEY_PREFIX}${conversationId}`;
  }

  static parseResumeRecord(raw: string | null): IDataRoomResumeRecord | null {
    if (!raw) return null;
    try {
      const parsed: unknown = JSON.parse(raw);
      if (typeof parsed !== 'object' || parsed === null) return null;
      // Safe: a non-null object, and every field below is type-checked before use.
      const { uploadId, fileCount, lastModifiedByBatch } = parsed as Record<
        string,
        unknown
      >;
      if (typeof uploadId !== 'string' || typeof fileCount !== 'number')
        return null;
      const batches = Array.isArray(lastModifiedByBatch)
        ? lastModifiedByBatch.map((batch: unknown) =>
            Array.isArray(batch)
              ? batch.filter(
                  (value): value is number => typeof value === 'number'
                )
              : []
          )
        : [];
      return { uploadId, fileCount, lastModifiedByBatch: batches };
    } catch {
      return null;
    }
  }

  static readResumeRecord(storageKey: string): IDataRoomResumeRecord | null {
    if (typeof window === 'undefined') return null;
    return DataRoomResumeRecordHelper.parseResumeRecord(
      window.sessionStorage.getItem(storageKey)
    );
  }

  /** Falls back to a record without last-modified times when sessionStorage is full; resume then
   * pairs files by name and size only. */
  static saveResumeRecord(
    storageKey: string,
    record: Readonly<IDataRoomResumeRecord>
  ): void {
    try {
      window.sessionStorage.setItem(storageKey, JSON.stringify(record));
    } catch {
      try {
        window.sessionStorage.setItem(
          storageKey,
          JSON.stringify({ ...record, lastModifiedByBatch: [] })
        );
      } catch {
        // Without storage the upload still finishes in this tab; only a reload cannot resume it.
      }
    }
  }

  static clearResumeRecord(storageKey: string): void {
    window.sessionStorage.removeItem(storageKey);
  }
}
